import React, { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import NotificationToast, { NotificationToastData } from './NotificationToast';
import { useAuth } from '../hooks/useAuth';

const NotificationToastContainer: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationToastData[]>([]);
  const [, setSocket] = useState<Socket | null>(null);

  // Socket.IO must connect to the API server *origin* (NOT the REST baseURL like ".../api")
  const getSocketBaseUrl = () => {
    const raw =
      (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
      (process.env.REACT_APP_ENV === 'production'
        ? 'https://booking-i73o.onrender.com'
        : 'http://localhost:3001');

    // If it's a full URL, strip any path/query/hash by taking `origin`
    try {
      const url = new URL(raw);
      return url.origin;
    } catch {
      // Fallback for non-standard values: strip a trailing "/api" if present
      return raw.replace(/\/+$/, '').replace(/\/api$/i, '');
    }
  };

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // Notifications are filtered on the backend by receiverId
    // Only business owners and professionals (assigned to appointments) receive real-time notifications
    // Customers receive email confirmations but not real-time toast notifications for booking events

    const apiBaseUrl = getSocketBaseUrl();
    
    // Validate URL before connecting
    if (!apiBaseUrl || apiBaseUrl.trim() === '') {
      console.warn('🔔 Invalid API URL, skipping Socket.IO connection');
      return;
    }

    // Socket.IO connects to the default namespace
    // Use the full URL without any namespace path (default is '/')
    const newSocket = io(apiBaseUrl, {
      path: '/socket.io/', // Default Socket.IO path
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      reconnectionDelayMax: 5000,
      autoConnect: true,
      forceNew: false,
      // Add timeout to prevent hanging connections
      timeout: 20000,
      withCredentials: true,
    });

    newSocket.on('connect', () => {
      // Subscribe to notifications for this user
      newSocket.emit('subscribe-notifications', { userId: user.id });
    });

    newSocket.on('disconnect', () => {});

    newSocket.on('connect_error', (error: Error) => {
      // Only log if it's not a namespace error (which might be transient)
      if (!error.message.includes('Invalid namespace')) {
        console.error('🔔 Connection error:', error.message);
      }
      // Don't show error to user - it will auto-reconnect
    });

    newSocket.on('reconnect', (_attemptNumber) => {
      // Re-subscribe after reconnection
      if (user?.id) {
        newSocket.emit('subscribe-notifications', { userId: user.id });
      }
    });

    // Listen for new notifications
    newSocket.on('new-notification', (notification: NotificationToastData) => {
      // Only show if this notification is for the current user
      if (notification.receiverId === user.id) {
        setNotifications((prev) => {
          // Avoid duplicates
          if (prev.some(n => n.id === notification.id)) {
            return prev;
          }
          return [...prev, notification];
        });
      }
    });

    setSocket(newSocket);

    return () => {
      if (newSocket && newSocket.connected) {
        newSocket.disconnect();
      }
    };
  }, [user?.id]);

  const handleClose = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  return (
    <div
      className="
        fixed top-4 right-4 z-[9999]
        w-full max-w-sm
        pointer-events-none
        space-y-3
        px-4 sm:px-0
      "
      style={{ 
        maxHeight: 'calc(100vh - 2rem)', 
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <div className="pointer-events-auto space-y-3">
        {notifications.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onClose={() => handleClose(notification.id)}
            duration={7000} // 7 seconds
          />
        ))}
      </div>
    </div>
  );
};

export default NotificationToastContainer;

